/**
 * /auth/callback
 *
 * Magic-link return target. The link emitted by Supabase Auth (subject + body
 * in supabase/templates/magic_link.html) lands the browser here with a `code`
 * query param (PKCE flow, `flowType: 'pkce'` in lib/supabase.ts). This route
 * is the SOLE owner of the code->session exchange: the client sets
 * `detectSessionInUrl: false` precisely so the SDK does not auto-exchange the
 * one-time code and race this handler. We exchange the code here, persist the
 * session, then route by role.
 *
 * After the session lands, we read the role claim and route:
 *   - operator -> /eric
 *   - subscriber (or no role) -> /portal
 *
 * If the exchange fails, we route back to /auth/sign-in with a message.
 *
 * Per ARCH-002 §2 + T-017 AC: this is the only post-magic-link landing surface.
 */

import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { getCurrentSession, getPostSignInPath } from '@/lib/auth';
import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/auth/callback')({
  component: AuthCallbackPage,
});

function AuthCallbackPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'pending' | 'error'>('pending');
  const [message, setMessage] = useState('Signing you in...');
  // PKCE codes are one-time-use. Under React.StrictMode in development the
  // useEffect runs twice on mount; the second invocation would replay the
  // already-consumed code and surface a false "expired or already used"
  // error. The ref survives across both invocations (same mount) so we
  // exchange exactly once.
  const exchangeStarted = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function completeSignIn() {
      try {
        const codeFromUrl = new URLSearchParams(window.location.search).get('code');
        if (codeFromUrl && !exchangeStarted.current) {
          exchangeStarted.current = true;
          const { error } = await supabase.auth.exchangeCodeForSession(codeFromUrl);
          if (error) {
            if (cancelled) return;
            setStatus('error');
            setMessage(
              'That sign-in link did not work. It may have expired or already been used. Request a new one.',
            );
            return;
          }
        }

        const session = await getCurrentSession();
        if (cancelled) return;
        if (!session) {
          setStatus('error');
          setMessage(
            'No session found after sign-in. Try requesting a new link.',
          );
          return;
        }

        const next = getPostSignInPath(session);
        navigate({ to: next, replace: true });
      } catch (caught) {
        if (cancelled) return;
        setStatus('error');
        setMessage(
          caught instanceof Error
            ? caught.message
            : 'Something went wrong while signing you in.',
        );
      }
    }

    void completeSignIn();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-md space-y-4 text-center">
        <p className="font-display text-xl text-foreground">{message}</p>
        {status === 'error' ? (
          <a
            href="/auth/sign-in"
            className="text-sm text-primary underline underline-offset-4"
          >
            Back to sign-in
          </a>
        ) : null}
      </div>
    </main>
  );
}
